import React, { useState, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import api from '../api';
import { Calendar, Flag, Send } from 'lucide-react';
import { Sheet } from './AdminUi';

// Prijedlog zadatka (član koji nije admin): isti izgled kao „Novi zadatak”, ali samo naziv, vrijeme i opis.
// Admin prijedlog odobrava ili odbija; tek tada zadatak postaje vidljiv ostalima.

// Naslov dobiva fokus samo uz miš (desktop); na mobitelu tipkovnica se otvara tek na dodir polja.
const FINE_POINTER = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: fine)').matches;
const pad = (n) => String(n).padStart(2, '0');
const toInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const toIso = (v) => (v ? new Date(v).toISOString() : null);

function ProposeTaskModal({ onClose, onSave }) {
  const [title, setTitle] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [descDirty, setDescDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const closeRef = useRef(null);

  const editor = useEditor({ extensions: [StarterKit], content: '', onUpdate: () => setDescDirty(true) });

  const onStart = (v) => {
    if (v && start && end) {
      const gap = new Date(end) - new Date(start);
      if (gap >= 0) setEnd(toInput(new Date(new Date(v).getTime() + gap)));
    } else if (v && !end) {
      setEnd(toInput(new Date(new Date(v).getTime() + 3600000)));
    }
    setStart(v);
  };

  const missing = !title.trim() ? 'Upiši naziv'
    : !start ? 'Odaberi početak'
    : end && new Date(end) < new Date(start) ? 'Kraj je prije početka'
    : null;
  const dirty = !!title || !!start || !!end || descDirty;

  const send = async () => {
    if (missing || busy) return;
    setBusy(true); setError('');
    try {
      await api.post('/tasks/propose', {
        title: title.trim(),
        description: editor ? editor.getHTML() : '',
        start_date: toIso(start),
        end_date: toIso(end),
      });
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Slanje prijedloga nije uspjelo.');
      setBusy(false);
    }
  };

  return (
    <Sheet tall z={1300} onClose={onClose} dirty={dirty} busy={busy} closeRef={closeRef}
      discardText="Prijedlog još nije poslan i bit će izgubljen."
      head={
        <div className="adm-sh">
          <div className="adm-main">
            <h3>Predloži zadatak</h3>
            <p>Admin pregleda prijedlog i odobri ga ili odbije. Dobit ćeš obavijest o odluci.</p>
          </div>
        </div>
      }
      footer={
        <div className="adm-dirty">
          {error && <p style={{ color: '#ac463b' }}>{error}</p>}
          <div className="tf-foot">
            <button type="button" className="adm-btn" disabled={busy} onClick={() => closeRef.current && closeRef.current()}>Odustani</button>
            <button type="button" className={`adm-btn ${missing ? 'tf-off' : 'primary'}`} disabled={!!missing || busy} onClick={send}>
              {busy ? 'Šaljem…' : missing || <><Send size={16} /> Pošalji prijedlog</>}
            </button>
          </div>
        </div>
      }
    >
      <input id="pt-title" className="tf-title" value={title} onChange={e => setTitle(e.target.value)}
        placeholder="Što treba napraviti?" maxLength={100} autoFocus={FINE_POINTER} />

      <div className="adm-lbl">Kada</div>
      <div className="adm-card">
        <label className="tf-row">
          <span className="tf-ico"><Calendar size={17} /></span>
          <span className="tf-k">Početak</span>
          <input id="pt-start" type="datetime-local" className="tf-dt" value={start} onChange={e => onStart(e.target.value)} />
        </label>
        <label className="tf-row">
          <span className="tf-ico"><Flag size={17} /></span>
          <span className="tf-k">Kraj</span>
          <input id="pt-end" type="datetime-local" className="tf-dt" value={end} onChange={e => setEnd(e.target.value)} />
        </label>
      </div>

      <div className="adm-lbl">Opis</div>
      <div className="tiptap-editor tf-editor">
        <div className="tiptap-toolbar">
          <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={editor?.isActive('bold') ? 'is-active' : ''}>B</button>
          <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor?.isActive('italic') ? 'is-active' : ''}><i>I</i></button>
          <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor?.isActive('bulletList') ? 'is-active' : ''}>• Lista</button>
          <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor?.isActive('orderedList') ? 'is-active' : ''}>1. Lista</button>
        </div>
        <EditorContent editor={editor} />
      </div>
      <p className="adm-note">Opiši što treba, gdje i zašto. Broj ljudi, grupe i ostalo admin dopunjuje nakon odobravanja.</p>
    </Sheet>
  );
}

export default ProposeTaskModal;
